import { api, ACCOUNT_API_SUB_URLS, MEMBER_API_SUB_URLS, V2 } from '@/api';
import moment from 'moment';
import { dateFormat } from '@/constants';
import { EventBus } from '@/event-bus';
import { isNullOrUndefined } from 'util';
import { Module } from 'vuex/types';
import { AxiosResponse } from 'axios';
import {
  RootState,
  MemberAccountResponseModel,
  TransactionFilterParams,
  PostedTransactionResponseModel,
  PendingTransactionResponseModel,
  ScheduledTransactionResponseModel,
  HistoryTransactionResponseModel,
  AccountDetailResponseModel,
  AccountPlanTypes,
  AccountBeneficiaries,
  AccountBeneficiaryResponseModel,
  MemberTaskListResponseModel,
  UnsubstantiatedClaimResponseModel,
  MemberSegmentationQuestionResponseModel,
  AccountPlanTypeResponseModel,
} from '@/@types/member';

interface AccountState {
  currentAccount: MemberAccountResponseModel;
  accounts: MemberAccountResponseModel[];
  inactiveAccounts: MemberAccountResponseModel[];
  currentTransactionFilterParams: TransactionFilterParams;
  postedTransactions: PostedTransactionResponseModel[];
  pendingTransactions: PendingTransactionResponseModel[];
  scheduledTransactions: ScheduledTransactionResponseModel[];
  transactionHistory: HistoryTransactionResponseModel[];
  accountDetail: AccountDetailResponseModel;
  singleScheduledTransaction: ScheduledTransactionResponseModel;
  accountPlanTypes: AccountPlanTypes;
  accountBeneficiaries: AccountBeneficiaries;
  currentBeneficiaries: AccountBeneficiaryResponseModel[];
  taskList: MemberTaskListResponseModel[];
  unsubstantiatedClaims: UnsubstantiatedClaimResponseModel[];
  segmentationQuestions: MemberSegmentationQuestionResponseModel[];
}

const emptyAccountDetail: AccountDetailResponseModel = {
  accountId: null,
  threshold: null,
  accumulation: null,
  beginningBalance: null,
  spendingGoal: null,
  rollOver: null,
  election: null,
  employerFunding: null,
  currentTaxYearContributions: null,
  priorTaxYearContributions: null,
  contributions: null,
  claims: null,
  pendingAmount: null,
  unprocessedClaims: null,
  isPendingOpenInvAcct: false,
};

const ACCOUNT_STATE: Module<AccountState, RootState> = {
  state: {
    currentAccount: null,
    accounts: null,
    inactiveAccounts: [],
    currentTransactionFilterParams: {
      startDate: moment()
        .subtract(30, 'days')
        .format(dateFormat),
      endDate: moment()
        .add(5, 'days')
        .format(dateFormat),
    },
    postedTransactions: [],
    pendingTransactions: [],
    scheduledTransactions: [],
    transactionHistory: [],
    accountDetail: null,
    singleScheduledTransaction: null,
    accountPlanTypes: {},
    accountBeneficiaries: {},
    currentBeneficiaries: [],
    taskList: [],
    unsubstantiatedClaims: [],
    segmentationQuestions: [],
  },
  actions: {
    // UC-REST-008 - Get Member Accounts
    getAccounts(context): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/accounts`,
        )
        .then((accounts) => {
          // Convert date back to mm/dd/yyyy because all of our code expects that format
          accounts.data.map((account) => {
            account.startDate = account.startDate
              ? moment(account.startDate).format(dateFormat)
              : null;
            account.endDate = account.endDate
              ? moment(account.endDate).format(dateFormat)
              : null;
            account.termDate = account.termDate
              ? moment(account.termDate).format(dateFormat)
              : null;
            account.closeDate = account.closeDate
              ? moment(account.closeDate).format(dateFormat)
              : null;
            account.graceEndDate = account.graceEndDate
              ? moment(account.graceEndDate).format(dateFormat)
              : null;
            account.claimFilingDeadline = account.claimFilingDeadline
              ? moment(account.claimFilingDeadline).format(dateFormat)
              : null;
            account.investmentBalanceAsOfDate = account.investmentBalanceAsOfDate
              ? moment(account.investmentBalanceAsOfDate).format(dateFormat)
              : null;
            account.migrationDate = account.migrationDate
              ? moment(account.migrationDate).format(dateFormat)
              : null;
          });
          context.commit('setMemberAccounts', accounts.data);
          return accounts.data;
        });
    },
    // UC-REST-088 - Get Member Tasks
    getMemberTaskList(context): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/task-list`,
        )
        .then((tasks) => {
          context.commit('setMemberTaskList', tasks.data);
          return tasks.data;
        });
    },
    // UC-REST-086 - Get Member Segmentation Questions
    getMemberSegmentationQuestions(context): Promise<AxiosResponse> {
      return api
        .get(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/segmentation-questions${V2}`,
        )
        .then((questions) => {
          context.commit('setMemberSegmentationQuestions', questions.data);
          return questions.data;
        });
    },
    // UC-REST-035 - Add Scheduled Deposit
    addScheduledDeposit(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${MEMBER_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/scheduled-deposits`,
          options,
        )
        .then((deposit) => {
          return deposit.data;
        });
    },
    // UC-REST-084 Get Scheduled Transaction
    getSingleScheduledTransaction(
      context,
      transactionID,
    ): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.scheduledDeposits}`,
          {
            params: {
              memberId: context.getters.user.memberId,
              scheduledDepositId: transactionID,
            },
          },
        )
        .then((transaction) => {
          context.commit('setSingleScheduledTransaction', transaction.data[0]);
        });
    },
    // UC-REST-009 - Get Member Account Detail
    getAccountDetail(context, id): Promise<AxiosResponse> {
      return api
        .get(`${ACCOUNT_API_SUB_URLS.accounts}/${id}`)
        .then((account) => {
          // Preserve original REST-009 null format of unset properties
          context.commit('setAccountDetail', {...emptyAccountDetail, ...account.data});
        });
    },
    // UC-REST-007 - Get Member Recent Activity
    getMemberRecentActivity(context): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/activities`,
          {
            params: {
              chunkSize: 5,
            },
          },
        )
        .then((postedTransactions) => {
          context.commit('setTransactionHistory', postedTransactions.data);
        });
    },
    // UC-REST-010 - Get Member Account Recent Activity
    getAccountRecentActivity(context, accountId): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.accounts}/${accountId}/activities/recent`,
          {
            params: {
              chunkSize: 5,
            },
          },
        )
        .then((transactionHistory) => {
          context.commit('setTransactionHistory', transactionHistory.data);
        });
    },
    // UC-REST-084 - Get Scheduled Deposit
    getAccountScheduledTransactions(
      context,
      accountID,
    ): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.scheduledDeposits}`,
          {
            params: {
              memberId: context.getters.user.memberId,
              memberAccountId: accountID,
            },
          },
        )
        .then((response) => {
          if (isNullOrUndefined(response)) {
            response = { data: [] };
          }
          context.commit('setScheduledTransactions', response.data);
        })
        .catch((error) => {
          if (error.hasOwnProperty('status') && error.status !== 404) {
            EventBus.$emit('openModal', 'Error', {
              message: 'global_connection-error',
              action: {
                label: 'global_try-again',
                performAction() {
                  location.reload();
                },
              },
              hideCancel: true,
            });
            // If 404 that's ok since it means no deposits exist for member
          } else {
            context.commit('setScheduledTransactions', []);
          }
        });
    },
    // UC-REST-029 - Supporting
    setActivityTransactionFilterParams(context, params): void {
      context.commit('setCurrentTransactionFilterParams', params);
    },
    // This function still grabs all transaction types but with the new APIs for pages that use this
    getAccountTransactions({ dispatch }, accountID): Promise<any> {
      return Promise.all([
        dispatch('getAccountScheduledTransactions', accountID),
        dispatch('getPostedTransactions', accountID),
        dispatch('getPendingTransactions', accountID),
      ]);
    },
    // UC-REST-029 - Get Member Account Posted Transactions
    getPostedTransactions(context, accountID): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.accounts}/${accountID}/posted-transactions`,
          {
            params: context.state.currentTransactionFilterParams,
          },
        )
        .then((transactionActivity) => {
          context.commit('setPostedTransactions', transactionActivity.data);
        });
    },
    // UC-REST-029a - Get Unsubstantiated Claims
    getUnsubstantiatedClaims(context): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/unsubstantiated-transactions`,
        )
        .then((response) => {
          // If there are no records for the user, the API will return a 204
          // in that case we need to return an empty array
          context.commit('setUnsubstantiatedClaims', response.data || []);
          return response.data || [];
        });
    },
    // UC-REST-029c - Get Member Account Pending Transactions
    getPendingTransactions(context, accountID): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.accounts}/${accountID}/pending-transactions`,
        )
        .then((transactions) => {
          context.commit('setPendingTransactions', transactions.data);
        });
    },
    // UC-REST-076 - Delete Scheduled Deposit
    deleteScheduledDeposit(context, id): Promise<AxiosResponse> {
      return api.delete(
        `${ACCOUNT_API_SUB_URLS.scheduledDeposits}${V2}/${id}`,
      );
    },
    // UC-REST-077 - Delete Pending Investment
    deletePendingInvestment(context, id): Promise<AxiosResponse> {
      return api.delete(
        `${ACCOUNT_API_SUB_URLS.investments}${V2}/${id}`,
      );
    },
    // UC-REST-043 - Get Beneficiary Information
    getBeneficiaryInformation(context, memberAccountId): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/beneficiaries`, {
            params: {
              memberAccountId,
              viewUnmaskedSSN: true,
            },
          },
        )
        .then((beneficiaries) => {
          context.commit('setBeneficiaryInformation', {
            beneficiaries: beneficiaries.data,
            memberAccountId,
          });
          return beneficiaries.data;
        });
    },
    // UC-REST-071 - Add Beneficiary
    addBeneficiary(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${ACCOUNT_API_SUB_URLS.members}/${
            context.getters.user.memberId
          }/beneficiaries`,
          options.payload,
        )
        .then((beneficiary) => {
          return beneficiary.data;
        });
    },
    // UC-REST-044 - Update Beneficiary Information
    updateBeneficiaryInformation(context, options): Promise<AxiosResponse> {
      return api
        .patch(
          `${ACCOUNT_API_SUB_URLS.beneficiaries}${V2}/${
            options.id
          }`,
          options.payload,
        )
        .then((beneficiary) => {
          return beneficiary.data;
        });
    },
    // UC-REST-047b - Get Member Plan Type Options
    getMemberPlanTypeOptions(context, accountId): Promise<AxiosResponse> {
      return api
        .get(
          `${ACCOUNT_API_SUB_URLS.accounts}/${accountId}/plan-types${V2}`,
          {
            params: {
              memberId: context.getters.user.memberId,
            },
          },
        )
        .then((planTypes) => {
          context.commit('setAccountPlanTypes', {
            planTypes: planTypes.data,
            accountId,
          });
          return planTypes.data;
        });
    },
    // UC-REST-042 - Update Account Stop Indicator
    updateAccountStopIndicator(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${ACCOUNT_API_SUB_URLS.accounts}/${
            options.accountId
          }/stop`,
          {
            isOnHold: options.isOnHold,
          },
        )
        .then((accountStatus) => {
          return accountStatus.data;
        });
    },
    // UC-REST-041 - Update Plan Information
    updatePlanInformation(context, options): Promise<AxiosResponse> {
      return api
        .patch(
          `${ACCOUNT_API_SUB_URLS.accounts}/${
            options.accountId
          }/plan-types${V2}`,
          {
            planType: `${options.planType}`,
            memberId: context.getters.user.memberId,
          },
        )
        .then((planType) => {
          return planType.data;
        });
    },
    // UC-REST-079 - Update Account Tier
    updateAccountTier(context, options): Promise<AxiosResponse> {
      return api
        .post(
          `${ACCOUNT_API_SUB_URLS.accounts}/${
            options.accountId
          }/tier`,
          {
            coverageTier: options.coverageTier,
          },
        )
        .then((coverageTier) => {
          return coverageTier.data;
        });
    },
  },
  mutations: {
    setMemberAccounts(
      state: AccountState,
      accounts: MemberAccountResponseModel[],
    ): void {
      state.accounts = accounts;
    },
    setMemberTaskList(
      state: AccountState,
      tasks: MemberTaskListResponseModel[],
    ): void {
      state.taskList = tasks;
    },
    setMemberSegmentationQuestions(
      state: AccountState,
      questions: MemberSegmentationQuestionResponseModel[],
    ): void {
      state.segmentationQuestions = questions;
    },
    setCurrentTransactionFilterParams(
      state: AccountState,
      params: TransactionFilterParams,
    ): void {
      state.currentTransactionFilterParams = params;
    },
    setSingleScheduledTransaction(
      state: AccountState,
      transaction: ScheduledTransactionResponseModel,
    ): void {
      state.singleScheduledTransaction = transaction;
    },
    setTransactionHistory(
      state: AccountState,
      transactionHistory: HistoryTransactionResponseModel[],
    ): void {
      state.transactionHistory = transactionHistory;
    },
    setPostedTransactions(
      state: AccountState,
      postedTransactions: PostedTransactionResponseModel[],
    ): void {
      state.postedTransactions = postedTransactions;
    },
    setPendingTransactions(
      state: AccountState,
      pendingTransactions: PendingTransactionResponseModel[],
    ): void {
      state.pendingTransactions = pendingTransactions;
    },
    setScheduledTransactions(
      state: AccountState,
      scheduledTransactions: ScheduledTransactionResponseModel[],
    ): void {
      state.scheduledTransactions = scheduledTransactions;
    },
    setUnsubstantiatedClaims(
      state: AccountState,
      claims: UnsubstantiatedClaimResponseModel[],
    ): void {
      state.unsubstantiatedClaims = claims;
    },
    setAccountDetail(
      state: AccountState,
      accountDetail: AccountDetailResponseModel,
    ): void {
      state.accountDetail = accountDetail;
    },
    setAccountPlanTypes(state: AccountState, data: any): void {
      state.accountPlanTypes[data.accountId] = data.planTypes;
    },
    setBeneficiaryInformation(state: AccountState, data: any): void {
      state.currentBeneficiaries = data.beneficiaries;
      state.accountBeneficiaries[data.memberAccountId] = data.beneficiaries;
    },
  },
  getters: {
    currentAccount: (state) => (id): MemberAccountResponseModel => {
      if (state.accounts) {
        return state.accounts.find((account) => {
          return account.accountId == id; // tslint:disable-line
        });
      }
    },
    currentTransactionFilterParams(state): TransactionFilterParams {
      return state.currentTransactionFilterParams;
    },
    accountDetail(state): AccountDetailResponseModel {
      return state.accountDetail;
    },
    accounts(state): MemberAccountResponseModel[] {
      if (Array.isArray(state.accounts)) {
        return state.accounts.filter((account) => {
          return account.isActive;
        });
      } else {
        return [];
      }
    },
    singleScheduledTransaction(state): ScheduledTransactionResponseModel {
      return state.singleScheduledTransaction;
    },
    inactiveAccounts(state): MemberAccountResponseModel[] {
      if (Array.isArray(state.accounts)) {
        return state.accounts.filter((account) => {
          return !account.isActive;
        });
      } else {
        return [];
      }
    },
    transactionHistory(state): HistoryTransactionResponseModel[] {
      return state.transactionHistory;
    },
    transactionsByAccount: (state) => (
      id,
    ): HistoryTransactionResponseModel[] => {
      return state.transactionHistory.filter((transaction) => {
        return String(transaction.accountId) === String(id);
      });
    },
    postedTransactions(state): PostedTransactionResponseModel[] {
      return state.postedTransactions;
    },
    scheduledTransactions(state): ScheduledTransactionResponseModel[] {
      return state.scheduledTransactions;
    },
    pendingTransactions(state): PendingTransactionResponseModel[] {
      return state.pendingTransactions;
    },
    unsubstantiatedClaims(state): UnsubstantiatedClaimResponseModel[] {
      return state.unsubstantiatedClaims;
    },
    hasActiveHSA(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
              return account.productType === 'HSA' && account.isActive;
            });
      return INDEX > -1;
    },
    hasNonTRA(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
              return account.productType !== 'TRA' && account.isActive;
            });
      return INDEX > -1;
    },
    hasNonTRAandAAP(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
              return account.productType !== 'TRA'
                && account.productType !== 'AAP'
                && account.isActive;
            });
      return INDEX > -1;
    },
    hasNonMigrated(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
            return account.isMigratedInd !== true;
          });
      return INDEX > -1;
    },
    hasMigrated(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
            return account.isMigratedInd === true;
          });
      return INDEX > -1;
    },
    activeHSAAccounts(state): MemberAccountResponseModel[] {
      if (state.accounts) {
        return state.accounts.filter((account) => {
          return account.productType === 'HSA' && account.isActive;
        });
      } else {
        return [];
      }
    },
    hasActiveVEBA(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
              return account.productType === 'VEBA' && account.isActive;
            });
      return INDEX > -1;
    },
    activeVEBAAccounts(state): MemberAccountResponseModel[] {
      if (state.accounts) {
        return state.accounts.filter((account) => {
          return account.productType === 'VEBA' && account.isActive;
        });
      }
    },
    activeFSAAccounts(state): MemberAccountResponseModel[] {
      if (state.accounts) {
        return state.accounts.filter((account) => {
          return account.productType === 'FSA' && account.isActive;
        });
      }
    },
    hasAllowCards(state): boolean {
      const INDEX =
        !state.accounts || !state.accounts.length
          ? -1
          : state.accounts.findIndex((account) => {
              return account.allowCards;
            });
      return INDEX > -1;
    },
    activeHRAAccounts(state): MemberAccountResponseModel[] {
      if (state.accounts) {
        return state.accounts.filter((account) => {
          return account.productType === 'HRA' && account.isActive;
        });
      }
    },
    planTypes: (state) => (accountId): AccountPlanTypeResponseModel[] => {
      return state.accountPlanTypes[accountId];
    },
    currentBeneficiaries(state): AccountBeneficiaryResponseModel[] {
      return state.currentBeneficiaries;
    },
    beneficiaries: (state) => (
      accountId,
    ): AccountBeneficiaryResponseModel[] => {
      const beneficiaries = state.accountBeneficiaries[accountId];
      return beneficiaries ? beneficiaries : [];
    },
    beneficiaryQuantity: (state) => (accountId): number => {
      return state.accountBeneficiaries[accountId].length;
    },
    memberTaskList(state): MemberTaskListResponseModel[] {
      return state.taskList;
    },
    segmentationQuestions(state): MemberSegmentationQuestionResponseModel[] {
      return state.segmentationQuestions;
    },
  },
};

export default ACCOUNT_STATE;
